package com.siriusxm.pia.views.chat

import com.siriusxm.pia.SXMUI
import org.jetbrains.compose.web.css.*

/**
 * Styles for the HTML that [MarkdownContent] injects into an assistant message bubble.
 *
 * These have to be plain descendant rules on a literal class name rather than Compose styles: the
 * markdown is rendered to an HTML string and set as `innerHTML`, so the elements inside a bubble are
 * never composed and can't carry generated class names. `.chat-markdown` is the hook the bubble puts
 * on its container; everything below is styled by tag.
 *
 * Rendered once by `ConversationView`.
 */
object ChatStyles : StyleSheet() {
    init {
        // Trim the outer margins the browser puts on block children, so a single-paragraph reply
        // isn't padded away from the edges of its bubble, and multi-block replies stay compact.
        ".chat-markdown > *:first-child" style {
            marginTop(0.px)
        }

        ".chat-markdown > *:last-child" style {
            marginBottom(0.px)
        }

        ".chat-markdown p" style {
            margin(0.px, 0.px, 8.px, 0.px)
            property("line-height", "1.5")
        }

        ".chat-markdown ul, .chat-markdown ol" style {
            margin(0.px, 0.px, 8.px, 0.px)
            property("padding-inline-start", "22px")
        }

        ".chat-markdown li" style {
            property("line-height", "1.5")
            marginBottom(2.px)
        }

        ".chat-markdown h1, .chat-markdown h2, .chat-markdown h3, .chat-markdown h4" style {
            margin(12.px, 0.px, 6.px, 0.px)
            fontWeight(700)
            // Model replies arrive with arbitrary heading levels; inside a message bubble they are
            // emphasis, not document structure, so they stay close to body size.
            fontSize(1.05.em)
        }

        ".chat-markdown code" style {
            property("font-family", "monospace")
            fontSize(0.92.em)
            padding(1.px, 4.px)
            borderRadius(4.px)
            backgroundColor(SXMUI.mainLayoutBackgroundColor.value())
            border(1.px, LineStyle.Solid, SXMUI.borderDividerDefault.value())
        }

        // A fenced block scrolls rather than stretching the bubble: the panel is allowed to shrink
        // below its content's width (see ConversationView), so an unbreakable line has to be
        // contained here or it forces a horizontal scrollbar on the whole message area.
        ".chat-markdown pre" style {
            margin(0.px, 0.px, 8.px, 0.px)
            padding(10.px, 12.px)
            borderRadius(8.px)
            backgroundColor(SXMUI.mainLayoutBackgroundColor.value())
            border(1.px, LineStyle.Solid, SXMUI.borderDividerDefault.value())
            overflowX("auto")
        }

        ".chat-markdown pre code" style {
            padding(0.px)
            border(0.px)
            backgroundColor(Color.transparent)
        }

        ".chat-markdown blockquote" style {
            margin(0.px, 0.px, 8.px, 0.px)
            padding(0.px, 0.px, 0.px, 12.px)
            property("border-left", "3px solid ${SXMUI.borderDividerDefault.value()}")
            color(SXMUI.subtleTextColor.value())
        }

        // GFM tables. `display: block` with `overflow-x: auto` is what makes a wide table scroll
        // inside the bubble instead of widening it; a table is not a scroll container as
        // `display: table`.
        ".chat-markdown table" style {
            display(DisplayStyle.Block)
            overflowX("auto")
            property("width", "max-content")
            maxWidth(100.percent)
            margin(0.px, 0.px, 8.px, 0.px)
            property("border-collapse", "collapse")
            fontSize(0.95.em)
        }

        ".chat-markdown th, .chat-markdown td" style {
            border(1.px, LineStyle.Solid, SXMUI.borderDividerDefault.value())
            padding(5.px, 9.px)
            property("text-align", "left")
            property("vertical-align", "top")
        }

        ".chat-markdown th" style {
            fontWeight(700)
            backgroundColor(SXMUI.mainLayoutBackgroundColor.value())
        }

        // Long cell values (ids, URLs) wrap instead of forcing the table wider than the bubble.
        ".chat-markdown td" style {
            property("overflow-wrap", "anywhere")
        }
    }
}
